import React, { FC, useMemo, useState } from 'react';
import { AutoComplete, Input, Modal } from 'antd';
import { Speciality } from '../../model/Speciality';

const ADHD_CARE_PATHWAY_URL = 'https://adhd.telecare.com.au/';

type Props = {
  specialities: Speciality[];
  selectedId: string | null;
  // eslint-disable-next-line no-unused-vars
  onSelect: (speciality: Speciality) => void;
};

const SpecialtyPicker: FC<Props> = ({ specialities, selectedId, onSelect }) => {
  const [isAdhdInfoModalOpen, setIsAdhdInfoModalOpen] = useState(false);
  const adhdSpecialtyId = process.env.REACT_APP_ADHD_SPECIALTY_ID;

  const handleClick = (speciality: Speciality) => {
    // ADHD/ASD has its own booking journey.
    if (adhdSpecialtyId && speciality.id === adhdSpecialtyId) {
      setIsAdhdInfoModalOpen(true);
      return;
    }
    onSelect(speciality);
  };

  const closeModal = () => setIsAdhdInfoModalOpen(false);

  const [query, setQuery] = useState('');
  const bookable = useMemo(() => specialities.filter(({ specialistCount }) => specialistCount > 0), [specialities]);
  const searchOptions = useMemo(() => {
    const text = query.trim().toLowerCase();
    return bookable
      .filter(({ name }) => name.toLowerCase().startsWith(text))
      .map(({ id, name }) => ({ label: name, value: id }));
  }, [bookable, query]);

  const handleSearchSelect = (id: string) => {
    const speciality = bookable.find((item) => item.id === id);
    setQuery('');
    if (speciality) handleClick(speciality);
  };

  return (
    <>
      <AutoComplete
        value={query}
        options={searchOptions}
        onSearch={setQuery}
        onSelect={handleSearchSelect}
        className="search"
        style={{ width: '100%' }}
      >
        <Input.Search placeholder="Search for a specialty" aria-label="Search for a specialty" />
      </AutoComplete>
      <div className="cardgrid">
        {specialities.map((speciality) => (
          <button
            key={speciality.id}
            type="button"
            className={`chip${speciality.id === selectedId ? ' on' : ''}`}
            aria-pressed={speciality.id === selectedId}
            disabled={speciality.specialistCount <= 0}
            onClick={() => handleClick(speciality)}
          >
            <b>{speciality.name}</b>
            <span>{`${speciality.specialistCount} clinician${speciality.specialistCount === 1 ? '' : 's'}`}</span>
          </button>
        ))}
      </div>
      <Modal
        title="ADHD/ASD assessment"
        visible={isAdhdInfoModalOpen}
        onCancel={closeModal}
        centered
        footer={(
          <>
            <button type="button" className="btn btn-g" onClick={closeModal}>Cancel</button>
            <a className="btn btn-p" href={ADHD_CARE_PATHWAY_URL} target="_blank" rel="noopener noreferrer" onClick={closeModal}>
              Go to ADHD/ASD booking
            </a>
          </>
        )}
      >
        <p className="psub" style={{ marginBottom: 0 }}>
          ADHD and ASD assessments have their own booking process. Visit our care pathway page to learn more and book your appointment.
        </p>
      </Modal>
    </>
  );
};

export default SpecialtyPicker;
